/**
 * Chat sound effects — synthesized via Web Audio API so no binary assets
 * need to ship with the bundle. Both effects respect the user's
 * `chatPreferences` toggles; pass the live preference value at call site.
 *
 * Two effects:
 *   playSendSound()    — short rising blip (~90 ms) on outgoing message
 *   playReceiveSound() — two-tone descending "ding" (~180 ms) on inbound msg
 *
 * The AudioContext is created lazily on the first call to avoid the autoplay
 * policy warning before any user gesture. Browsers that suspend the context
 * after tab inactivity are resumed transparently. Failure to play is silent
 * — a missing sound should never block message flow.
 */

let _ctx: AudioContext | null = null;

function getContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (_ctx) return _ctx;
  const Ctor =
    (window as any).AudioContext || (window as any).webkitAudioContext;
  if (!Ctor) return null;
  try {
    _ctx = new Ctor();
    return _ctx;
  } catch {
    return null;
  }
}

async function ensureRunning(ctx: AudioContext): Promise<boolean> {
  if (ctx.state === 'suspended') {
    try {
      await ctx.resume();
    } catch {
      return false;
    }
  }
  return ctx.state === 'running';
}

type Tone = {
  /** Hz */
  frequency: number;
  /** Seconds from `t0` */
  startOffset: number;
  /** Seconds */
  duration: number;
  /** Linear gain peak (0..1) */
  gain: number;
  type?: OscillatorType;
};

async function playTones(tones: Tone[]): Promise<void> {
  const ctx = getContext();
  if (!ctx) return;
  if (!(await ensureRunning(ctx))) return;
  const t0 = ctx.currentTime;
  for (const t of tones) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = t.type ?? 'sine';
    osc.frequency.value = t.frequency;
    // Short attack + linear release so each tone stays soft, no clicks.
    gain.gain.setValueAtTime(0, t0 + t.startOffset);
    gain.gain.linearRampToValueAtTime(t.gain, t0 + t.startOffset + 0.012);
    gain.gain.linearRampToValueAtTime(
      0,
      t0 + t.startOffset + t.duration,
    );
    osc.connect(gain).connect(ctx.destination);
    osc.start(t0 + t.startOffset);
    osc.stop(t0 + t.startOffset + t.duration + 0.02);
  }
}

/** Outgoing "whoosh up" — single ascending sine, ~90 ms. */
export function playSendSound(): void {
  void playTones([
    { frequency: 520, startOffset: 0, duration: 0.045, gain: 0.10 },
    { frequency: 780, startOffset: 0.035, duration: 0.06, gain: 0.10 },
  ]);
}

/** Incoming message — two-tone descending "ding", ~180 ms. */
export function playReceiveSound(): void {
  void playTones([
    { frequency: 880, startOffset: 0, duration: 0.09, gain: 0.12, type: 'triangle' },
    { frequency: 660, startOffset: 0.07, duration: 0.13, gain: 0.10, type: 'triangle' },
  ]);
}
